import React, { Fragment, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";

import GhxpArrows from "./GhxpArrows";
import { MAPS_URL } from "./ghxpEvent";
import logoGh from "../../../assets/img/ghExperience/logo-gh-white.png";
import logoRemus from "../../../assets/img/ghExperience/logo-remus.svg";

// Peças compartilhadas da LP: tipografia, dados do evento, assinatura, grafismos, palco de mídia e botões.

const FINE_POINTER = window.matchMedia("(pointer: fine)");

// Rótulo pequeno em caixa alta precedido do traço laranja (Projeto Visual, pág. 7).
export function Eyebrow({ children, className = "" }) {
  return <p className={`ghxpEyebrow ${className}`}>{children}</p>;
}

// Divide uma string de tradução (com <strong> e <br/>) em palavras, para os títulos cinéticos.
export function kineticWords(str) {
  const words = [];
  let strong = false;
  String(str)
    .split(/(<br\s*\/?>)|\s+/)
    .filter(Boolean)
    .forEach((chunk) => {
      if (/^<br/.test(chunk)) {
        words.push({ br: true });
        return;
      }
      const parts = [];
      chunk.split(/(<\/?strong>)/).forEach((segment) => {
        if (segment === "<strong>") strong = true;
        else if (segment === "</strong>") strong = false;
        else if (segment) parts.push({ text: segment, strong });
      });
      if (parts.length) words.push({ parts });
    });
  return words;
}

// Título cinético: cada palavra entra em sequência (como a tagline do convite fechado) quando o slide assenta.
export function KineticTitle({ text, as: Tag = "h2", className = "", ...props }) {
  const words = kineticWords(text);
  let order = 0;
  return (
    <Tag className={`ghxpKinetic ${className}`} {...props}>
      {words.map((word, i) =>
        word.br ? (
          <br key={i} />
        ) : (
          <Fragment key={i}>
            {i > 0 && !words[i - 1].br ? " " : null}
            <span className="ghxpKinetic__word" style={{ "--w": order++ }}>
              {word.parts.map((part, j) => (part.strong ? <strong key={j}>{part.text}</strong> : <Fragment key={j}>{part.text}</Fragment>))}
            </span>
          </Fragment>
        )
      )}
    </Tag>
  );
}

// Palco de mídia compartilhado pelas cenas: inclina com o mouse, luz que segue o cursor,
// sombra na direção do texto e a seta dupla que se desenha quando o slide assenta.
export function SceneStage({ children, shade = "hero", className = "", arrows = true }) {
  return (
    <div className={`ghxpStage ${className}`} aria-hidden="true">
      <div className="ghxpStage__tilt">{children}</div>
      <div className="ghxpStage__light" />
      <div className={`ghxpStage__shade ghxpStage__shade--${shade}`} />
      {arrows && <GhxpArrows variant="outline" className="ghxpStage__arrows" />}
    </div>
  );
}

// Inclinação 3D de um cartão seguindo o mouse (ponteiro fino, fora do modo de baixo consumo).
export const tiltHandlers = {
  onMouseMove: (event) => {
    const card = event.currentTarget;
    if (!FINE_POINTER.matches || card.closest(".is-low-power")) return;
    const rect = card.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;
    card.style.setProperty("--tx", (x * 100).toFixed(1));
    card.style.setProperty("--ty", (y * 100).toFixed(1));
    card.style.transform = `perspective(900px) rotateX(${(-y * 7).toFixed(2)}deg) rotateY(${(x * 9).toFixed(2)}deg) translateZ(8px)`;
  },
  onMouseLeave: (event) => {
    event.currentTarget.style.transform = "";
  },
};

// Frase-conceito "MAIS LONGE, JUNTOS." com o espaçamento largo do brandbook (pág. 6).
export function Tagline({ className = "", as: Tag = "p" }) {
  return (
    <Tag className={`ghxpTagline ${className}`}>
      <Trans i18nKey="ghxp_tagline" />
    </Tag>
  );
}

// Dados do evento em colunas com a barra laranja (como no convite digital).
export function EventInfo({ className = "" }) {
  return (
    <div className={`ghxpInfo ${className}`}>
      <p className="ghxpInfo__item">
        <Trans i18nKey="ghxp_info_date" />
      </p>
      <p className="ghxpInfo__item">
        Experience House
        <br />
        <strong>São Paulo</strong>
      </p>
      <p className="ghxpInfo__item">
        <strong>Rua</strong> Gerivatiba, 207,
        <br />
        Rooftop, <strong>Butantã</strong>
      </p>
    </div>
  );
}

// Assinatura: "Realização" GH + "Apoio" Remus Logistics, com menos destaque (pág. 27).
export function Signature({ className = "", withSupport = true }) {
  const { t } = useTranslation();
  return (
    <div className={`ghxpSignature ${className}`}>
      <span className="ghxpSignature__label">{t("ghxp_signature")}</span>
      <img className="ghxpSignature__gh" src={logoGh} alt="GH Solucionador Logístico" width="420" height="112" />
      {withSupport && <span className="ghxpSignature__label ghxpSignature__label--support">{t("ghxp_support")}</span>}
      <img className="ghxpSignature__remus" src={logoRemus} alt="Remus Logistics" />
    </div>
  );
}

// Marcador de página do brandbook: retângulo laranja + "2026".
export function PageMark({ className = "" }) {
  return (
    <p className={`ghxpPageMark ${className}`} aria-hidden="true">
      <span />
      2026
    </p>
  );
}

// Botão principal (pág. 21) com a seta dupla sólida como ícone.
// Com mouse, o botão "atrai" levemente o cursor (efeito magnético), exceto no modo de baixo consumo.
export function CtaButton({ children, className = "", type = "button", ...props }) {
  const ref = useRef(null);

  const onMouseMove = (event) => {
    if (!FINE_POINTER.matches || ref.current.closest(".is-low-power")) return;
    const rect = ref.current.getBoundingClientRect();
    const dx = event.clientX - (rect.left + rect.width / 2);
    const dy = event.clientY - (rect.top + rect.height / 2);
    ref.current.style.transform = `translate(${(dx * 0.18).toFixed(1)}px, ${(dy * 0.28).toFixed(1)}px)`;
  };

  const onMouseLeave = () => {
    ref.current.style.transform = "";
  };

  return (
    <button
      ref={ref}
      type={type}
      className={`ghxpButton ${className}`}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      {...props}
    >
      <span>{children}</span>
      <GhxpArrows variant="solid" size="sm" className="ghxpButton__icon" />
    </button>
  );
}

// Link de mapa (Google Maps em nova aba).
export function MapLink({ className = "" }) {
  const { t } = useTranslation();
  return (
    <a className={`ghxpMapLink ${className}`} href={MAPS_URL} target="_blank" rel="noreferrer">
      {t("ghxp_venue_map")}
    </a>
  );
}

// Dica de rolagem: seta dupla pontilhada apontando para baixo + rótulo.
export function ScrollHint({ label, onClick, className = "", ...props }) {
  return (
    <button type="button" className={`ghxpScrollHint ${className}`} onClick={onClick} {...props}>
      <GhxpArrows variant="line" size="sm" className="ghxpScrollHint__icon" />
      <span>{label}</span>
    </button>
  );
}

// Foto de tela cheia: recorte vertical no celular, srcset no desktop; nunca ampliada acima do original.
// Carrega assim que é montada: quem decide a hora é o near() de cada slide (até dois passos à frente),
// para a foto já estar pronta quando o slide entrar.
export function Photo({ photo, alt, className = "", position, priority = false, sizes = "100vw" }) {
  return (
    <picture className={`ghxpPhoto ${className}`}>
      {photo.mobile && <source media="(max-width: 767px)" srcSet={photo.mobile.src} />}
      <img
        src={photo.src}
        srcSet={photo.srcSet}
        sizes={sizes}
        width={photo.width}
        height={photo.height}
        alt={alt}
        decoding="async"
        fetchpriority={priority ? "high" : undefined}
        style={position ? { objectPosition: position } : undefined}
      />
    </picture>
  );
}
